import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { useAuthStore } from "@app/store";
import { useLogoutApi } from "@services/auth";

/**
 * Log out, completely.
 *
 * Three things have to happen, and only one of them is the API call: the
 * server request, clearing the persisted session, and dropping the React Query
 * cache — without the last, the next user to sign in on this browser briefly
 * sees the previous user's data.
 *
 * The local teardown runs in `onSettled`, so it happens even when the request
 * fails. That is deliberate: with `JWT_BLACKLIST_ENABLED=false` the token stays
 * valid server-side until it expires anyway, so the client-side clear is what
 * actually ends the session — refusing to log out because the network was down
 * would be worse than useless.
 *
 * Composed here rather than in `services/auth` because it reaches into the
 * global store and the router, which the service layer may not — the same
 * reason `LoginPage` and `RegisterPage` sit in this feature.
 */
export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearSession = useAuthStore((state) => state.logout);

  const teardown = useCallback(() => {
    clearSession();
    queryClient.clear();
    navigate(PagesRoutes.auth.login, { replace: true });
  }, [clearSession, queryClient, navigate]);

  const logoutApi = useLogoutApi({
    withoutToastMessage: true,
    navigateOnError: false,
    onSettled: teardown,
  });

  return {
    logout: () => logoutApi.mutate(),
    isPending: logoutApi.isPending,
  };
}
